import { Component, DestroyRef, EventEmitter, inject, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Timestamp } from '@angular/fire/firestore';
import { AppUpdate } from '@capawesome/capacitor-app-update';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { delay } from 'rxjs/operators';
import { SubSink } from 'subsink';
import { AutoUnsubscribe } from '../auto-unsubscribe.decorator';
import { EmptyGridContainerComponent } from '../empty-grid-container/empty-grid-container.component';
import { MobileUrlReplacer } from '../pipe/mobile-url-replacer';
import { TimeAgoPipe } from '../pipe/time-ago.pipe';
import { UserNotification } from './system-notification.dto';
import { SystemNotificationService } from './system-notification.service';

@AutoUnsubscribe()
@Component({
  imports: [CommonModule, MatButtonModule, MatIconModule, TranslatePipe, MobileUrlReplacer, TimeAgoPipe, EmptyGridContainerComponent],
  selector: 'app-system-notification-center',
  templateUrl: './system-notification-center.component.html',
  styleUrls: ['./system-notification-center.component.scss'],
  standalone: true,
})
export class SystemNotificationCenterComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly pendingTimeouts: ReturnType<typeof setTimeout>[] = [];

  @Output()
  closeNotificationCenter = new EventEmitter<boolean>();

  @Input()
  active = false;

  @Input()
  set notifications(notifications: UserNotification[]) {
    this._notifications = notifications.sort((a, b) => ((a.audit?.createdDate as Timestamp).toDate() < (b.audit?.createdDate as Timestamp).toDate() ? 1 : -1));
    if (this.active) {
      this.markAllAsReadSub.next(true);
    }
  }

  get notifications(): UserNotification[] {
    return this._notifications;
  }
  private _notifications: UserNotification[] = [];

  private markAllAsReadSub = new Subject<boolean>();
  private readonly viewSubs = new SubSink();

  constructor(private readonly systemNotificationService: SystemNotificationService) {
    this.destroyRef.onDestroy(() => this.pendingTimeouts.forEach(clearTimeout));
    this.viewSubs.add(
      this.markAllAsReadSub.pipe(delay(5000)).subscribe(async () => {
        if (this.notifications?.length > 0) {
          const user = this.notifications[0].user;
          await this.systemNotificationService.markAllNotificationsAsRead(user._id);
        }
      }),
    );
  }

  onNotificationHover(userNotification: UserNotification, highlight: boolean): void {
    const matchingNotification = this.notifications.findIndex((n) => n._id === userNotification._id);
    if (matchingNotification < 0) {
      return;
    }
    this.notifications[matchingNotification].highlighted = highlight;
  }

  removeNotification(userNotification: UserNotification): void {
    const matchingNotification = this.notifications.findIndex((n) => n._id === userNotification._id);
    if (matchingNotification < 0) {
      return;
    }
    this._notifications[matchingNotification].remove = true;
    const id = setTimeout(async () => {
      const timeoutIndex = this.pendingTimeouts.indexOf(id);
      if (timeoutIndex >= 0) {
        this.pendingTimeouts.splice(timeoutIndex, 1);
      }
      const currentIndex = this._notifications.findIndex((n) => n._id === userNotification._id);
      if (currentIndex >= 0) {
        this._notifications.splice(currentIndex, 1);
      }
      await this.systemNotificationService.deleteNotification(userNotification);
    }, 1000);
    this.pendingTimeouts.push(id);
  }

  async deleteAll(): Promise<void> {
    const promises: Promise<void>[] = [];
    for (const notification of this._notifications) {
      notification.remove = true;
      promises.push(this.systemNotificationService.deleteNotification(notification));
    }
    // Wait til animation completes before clearing notifications
    const id = setTimeout(async () => {
      const timeoutIndex = this.pendingTimeouts.indexOf(id);
      if (timeoutIndex >= 0) {
        this.pendingTimeouts.splice(timeoutIndex, 1);
      }
      this._notifications = [];
      await Promise.all(promises);
    }, 1000);
    this.pendingTimeouts.push(id);
  }

  async openAppStore(userNotification: UserNotification): Promise<void> {
    if (userNotification.mobileOnly) {
      await AppUpdate.openAppStore();
    }
  }

  createdDate(n: UserNotification): Date {
    return (n.audit.createdDate as Timestamp).toDate();
  }
}
